import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FlappyCanvas from '../components/FlappyCanvas';
import '../styles/Flappy.css';

function makeSeed() {
  return `solo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function FlappySolo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawBotName = String(searchParams.get('botName') || '').trim();
  const botName = rawBotName ? rawBotName.slice(0, 24) : '';
  const { user } = useAuth();
  const [seed, setSeed] = useState(() => makeSeed());
  const [lastRun, setLastRun] = useState(null);

  // Endless run: no time limit; the round ends when you crash.
  const durationSeconds = 0;

  const subtitle = useMemo(() => {
    if (!lastRun) {
      return botName
        ? `No queue match found • Training vs ${botName} • Endless run`
        : 'Single-player • Endless run • Difficulty ramps up';
    }
    const coinsPart = Number.isFinite(lastRun.coinsCollected) ? ` • Coins: ${lastRun.coinsCollected}` : '';
    return `Last score: ${lastRun.score}${coinsPart}${lastRun.endedReason ? ` • ${lastRun.endedReason}` : ''}`;
  }, [lastRun, botName]);

  return (
    <div className="flappy-page">
      <div className="flappy-header">
        <div>
          <h1 className="flappy-title">{botName ? `🐦 Flappy vs ${botName}` : '🐦 Flappy (Solo)'}</h1>
          <div className="flappy-subtitle">{subtitle}</div>
        </div>
        <div className="flappy-header-actions">
          <button className="flappy-btn" onClick={() => navigate(user ? '/dashboard' : '/')}>← Dashboard</button>
          <button className="flappy-btn" onClick={() => navigate(user ? '/flappy' : '/login')}>Modes</button>
          <button className="flappy-btn" onClick={() => navigate(user ? '/flappy/duel' : '/login')}>Betting Mode</button>
          <button className="flappy-btn" onClick={() => { setLastRun(null); setSeed(makeSeed()); }}>New Round</button>
        </div>
      </div>

      <div className="flappy-grid">
        <section className="flappy-card">
          <h2>How to play</h2>
          <div className="flappy-meta">
            <div><strong>Controls:</strong> Click / Tap / Space / ↑ / W</div>
            <div><strong>Goal:</strong> Fly through pipes and score points</div>
            <div><strong>Round:</strong> Endless (ends on crash)</div>
            <div><strong>Difficulty:</strong> Speed increases + pipes tighten as you score</div>
          </div>
          {lastRun && (
            <div className="flappy-hint">
              Finished with <strong>{lastRun.score}</strong>
              {Number.isFinite(lastRun.coinsCollected) ? <> • Coins: <strong>{lastRun.coinsCollected}</strong></> : null}
              {' '}({lastRun.endedReason || 'ended'}).
            </div>
          )}
        </section>

        <section className="flappy-card">
          <h2>Play</h2>
          <FlappyCanvas
            seed={seed}
            durationSeconds={durationSeconds}
            mode="play"
            disabled={false}
            allowPractice={false}
            ghostRun={null}
            onFinish={({ score, coinsCollected, endedReason, endedAtMs }) => {
              setLastRun({ score, coinsCollected, endedReason, endedAtMs });
            }}
          />
          <div className="flappy-hint">Tip: click the canvas once, then use Space.</div>
        </section>
      </div>
    </div>
  );
}
